/* eslint-disable max-len */
/**
 * @fileoverview Modern Edit Post Modal - Premium Redesign
 *               PATH: src/components/common/EditPostModal.js
 *               REDESIGNED: Split-screen layout with integrated Visual Studio
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc, serverTimestamp, Timestamp, getDoc } from 'firebase/firestore';
import { db, storage } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import VisualGenerationPanel from '../contenthub/VisualGenerationPanel';
import VideoGenerationPanel from '../contenthub/VideoGenerationPanel';
import VisualBadge from './VisualBadge';
import { isVideoTemplate } from '../shared/utils/mediaTypeUtils';
import { 
    FiSave, FiLoader, FiTrash2, FiAlertCircle, FiX,
    FiUploadCloud, FiZap, FiFacebook, FiInstagram, FiRefreshCw, FiCheckCircle, FiClock
} from 'react-icons/fi';

const MAX_IMAGE_SIZE_MB = 10;
const generateImageFromPromptFn = httpsCallable(functions, 'generateImageFromPrompt');
const deployFn = httpsCallable(functions, 'postContentNow', { timeout: 300000 });

function EditPostModal({ project, post, onClose, initialPlatform }) {
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();

    // Standardize platform key
    const platform = (initialPlatform || 'facebook').toLowerCase();

    // --- State ---
    const [formData, setFormData] = useState({ 
        content: '', 
        visual: null,
        scheduledTime: '' 
    });
    
    const [activeVisualTab, setActiveVisualTab] = useState('current');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [localError, setLocalError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    // Carousel-specific state
    const [activePlatform, setActivePlatform] = useState(platform);

    useEffect(() => {
        setActivePlatform(platform);
    }, [platform]);

    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const [processingSlide, setProcessingSlide] = useState(null);
    
    const fileInputRef = useRef(null);
    const activeUploadTask = useRef(null);
    const prevPostIdRef = useRef(post?.id);
    const isMountedRef = useRef(true);

    // Track mutable visual data in state instead of mutating the post prop
    const [localVisualData, setLocalVisualData] = useState({
        visualSequence: post?.visualSequence || null,
        visualStatus: post?.visualStatus || null,
        visual: post?.visual || null,
    });

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (activeUploadTask.current) {
                activeUploadTask.current.cancel();
            }
        };
    }, []);

    useEffect(() => {
        if (post) {
            setLocalVisualData({
                visualSequence: post.visualSequence || null,
                visualStatus: post.visualStatus || null,
                visual: post.visual || null,
            });
        }
    }, [post]);

    // --- 1. Initialize Form ---
    useEffect(() => {
        if (!post) return;

        const isNewPost = post.id !== prevPostIdRef.current;
        if (isNewPost) {
            prevPostIdRef.current = post.id;
            setActiveVisualTab('current');
            setLocalError('');
        }

        let dateStr = '';
        if (post.scheduledTime?.toDate) {
            const d = post.scheduledTime.toDate();
            const offset = d.getTimezoneOffset() * 60000;
            dateStr = new Date(d.getTime() - offset).toISOString().slice(0, 16);
        }

        // Text Caption Isolation
        let initialContent = '';
        if (typeof post.content === 'object' && post.content !== null) {
            initialContent = post.content[platform] || '';
        } else if (typeof post.content === 'string') {
            initialContent = post.content;
        }

        // Visual Asset Isolation
        let initialVisual = null;
        if (post.visual) {
            if (post.visual[platform]) {
                initialVisual = post.visual[platform];
            } else if (post.visual.url) {
                initialVisual = post.visual;
            }
        }

        setFormData({
            content: initialContent,
            visual: initialVisual,
            scheduledTime: dateStr
        });
        
    }, [post, platform, initialPlatform]);

    const handleContentChange = (e) => setFormData(prev => ({ ...prev, content: e.target.value }));
    const handleDateChange = (e) => setFormData(prev => ({ ...prev, scheduledTime: e.target.value }));

    // --- 2. Manual Asset Upload ---
    const handleFileSelectAndUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file || !project || !post) return;

        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            setLocalError(`File size exceeds ${MAX_IMAGE_SIZE_MB}MB limit.`);
            return;
        }
        
        setLocalError('');
        setIsUploading(true);

        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const fileName = `${platform}_visual.${fileExt}`;
        const storagePath = `projectVisuals/${project.userId}/${project.id}/posts/${post.id}/${fileName}`;
        
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);
        activeUploadTask.current = uploadTask;

        uploadTask.on('state_changed',
            (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
            (error) => {
                if (error.code !== 'storage/canceled') { setLocalError("Upload failed. Please try again."); }
                setIsUploading(false);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                const visualData = { 
                    type: 'upload', 
                    url: downloadURL, 
                    metadata: { storagePath, name: file.name, size: file.size, type: file.type } 
                };
                setFormData(prev => ({...prev, visual: visualData}));
                setActiveVisualTab('current'); 
                setIsUploading(false);
            }
        );
    };

    // --- 3. Save Logic ---
    const handleSaveChanges = async () => {
        if (isSaving || isUploading) return;
        setIsSaving(true);
        setLocalError('');

        const postRef = doc(db, 'projects', project.id, 'posts', post.id);

        try {
            // A. File Cleanup
            const currentVisualRecord = localVisualData.visual?.[platform] || (localVisualData.visual?.url ? localVisualData.visual : null);
            const oldPath = currentVisualRecord?.metadata?.storagePath;
            const newPath = formData.visual?.metadata?.storagePath;

            if (oldPath && oldPath !== newPath) {
                try {
                    await deleteObject(ref(storage, oldPath));
                } catch (delErr) { console.warn("Cleanup failed", delErr); }
            }

            // Refetch the post to use Firestore as the merge base for visuals.
            // The cloud function image-merges across required platforms (FB+IG share one
            // generated image), but local state may only know the active platform.
            let freshVisual = localVisualData.visual;
            let freshSequence = localVisualData.visualSequence;
            try {
                const freshDoc = await getDoc(postRef);
                if (freshDoc.exists()) {
                    const fresh = freshDoc.data();
                    freshVisual = fresh.visual || freshVisual;
                    freshSequence = fresh.visualSequence || freshSequence;
                }
            } catch (refetchErr) { /* fall back to local state */ }

            // B. Surgical Update Preparation
            const updateData = {
                updatedAt: serverTimestamp(),
                lastAttemptError: null
            };

            // FIX: Avoid dot-notation field paths — build full objects instead
            if (typeof post.content === 'object' && post.content !== null) {
                updateData.content = { ...post.content, [platform]: formData.content };
                updateData.visual = { ...(freshVisual || {}), [platform]: formData.visual };
            } else {
                updateData.content = formData.content;
                updateData.visual = formData.visual;
            }

            // C. Strict Status Evaluation
            const isMeta = project.platform.toLowerCase().includes('meta');

            const checkMap = { ...(freshVisual || {}) };
            if (formData.visual) checkMap[platform] = formData.visual;
            else delete checkMap[platform];

            let isReady = false;

            const isPlatformReady = (p) => {
                const hasSingle = !!(checkMap[p]?.url || checkMap.url);
                const pTmpl = post.adjustments?.templates?.[p] || 'standard';
                const pIsCarousel = pTmpl.includes('carousel');
                const pSeq = freshSequence?.[p] || [];
                const pCarouselReady = pIsCarousel && pSeq.length >= 2 && pSeq.every(s => s.visual?.url);

                return hasSingle || pCarouselReady;
            };
            
            if (isMeta) {
                const requiresFb = !!project.associatedPageId;
                const requiresIg = !!project.associatedInstagramId;

                const fbMet = !requiresFb || isPlatformReady('facebook');
                const igMet = !requiresIg || isPlatformReady('instagram');
                
                const hasMasterUrl = !!checkMap.url;
                isReady = hasMasterUrl || (fbMet && igMet);
            } else {
                isReady = isPlatformReady(platform);
            }

            updateData.status = isReady ? 'scheduled' : 'draft';
            
            const currentVisualStatus = localVisualData.visualStatus;
            let newVisualStatus = isReady ? 'ready' : 'none';
            if (currentVisualStatus === 'queued' && !isReady) {
                newVisualStatus = 'queued'; 
            }
            updateData.visualStatus = newVisualStatus;

            if (!post.visualSettings && project.defaultVisualSettings) {
                updateData.visualSettings = project.defaultVisualSettings;
            }

            // D. Handle Date
            if (formData.scheduledTime) {
                const newDate = new Date(formData.scheduledTime);
                if (!isNaN(newDate.getTime())) {
                    updateData.scheduledTime = Timestamp.fromDate(newDate);
                }
            }

            await updateDoc(postRef, updateData);
            addToast(`Updated! Post is now ${updateData.status}.`, 'success');
            onClose();
        } catch (err) {
            setLocalError("Save failed. Please try again.");
            addToast("Save failed. Please try again.", 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublishNow = async () => {
        if (isPublishing) return;
        const confirmed = await confirm({ title: 'Publish Now', message: 'Publish this post immediately?', confirmLabel: 'Publish', variant: 'danger' });
        if (!confirmed) return;

        setIsPublishing(true);
        setLocalError('');

        try {
            const postRef = doc(db, 'projects', project.id, 'posts', post.id);
            await updateDoc(postRef, {
                status: 'scheduled',
                attempts: 0,
                lastAttemptError: null,
                lastAttemptAt: null,
                updatedAt: serverTimestamp()
            });

            const result = await deployFn({
                projectId: project.id,
                postId: post.id,
                platform: activePlatform || platform
            });

            if (result.data?.success) {
                addToast('Post published successfully!', 'success');
                onClose();
            } else {
                throw new Error(result.data?.error || 'Publish failed');
            }
        } catch (err) {
            setLocalError('Publish failed. Please try again.');
            addToast('Publish failed. Please try again.', 'error');
        } finally {
            setIsPublishing(false);
        }
    };

    const handleVisualUpdateFromAI = useCallback(async (postId, newVisual, platform, newStatus, newSequence) => {
        if (postId === post.id) {
            // Refetch post: cloud function merges single visuals across required platforms
            // (e.g. FB+IG share one image). Local state would otherwise know only the active platform.
            try {
                const freshDoc = await getDoc(doc(db, 'projects', project.id, 'posts', postId));
                if (freshDoc.exists()) {
                    const fresh = freshDoc.data();
                    setLocalVisualData({
                        visualSequence: fresh.visualSequence || null,
                        visualStatus: fresh.visualStatus || null,
                        visual: fresh.visual || null,
                    });
                    if (!newSequence) {
                        setFormData(prev => ({ ...prev, visual: fresh.visual?.[platform] || newVisual }));
                    } else {
                        setFormData(prev => ({ ...prev, visual: null }));
                    }
                    setActiveVisualTab('current');
                    return;
                }
            } catch (err) {
                // Fall through to legacy local-only update
            }

            if (newSequence) {
                setLocalVisualData(prev => ({
                    ...prev,
                    visualSequence: newSequence,
                    visualStatus: newStatus || prev?.visualStatus,
                }));
                setFormData(prev => ({ ...prev, visual: null }));
            } else if (newVisual) {
                setLocalVisualData(prev => ({
                    ...prev,
                    visual: { ...(prev?.visual || {}), [platform]: newVisual },
                    visualStatus: newStatus || prev?.visualStatus,
                }));
                setFormData(prev => ({ ...prev, visual: newVisual }));
            }
        } else {
             setFormData(prev => ({ ...prev, visual: newVisual }));
        }

        setActiveVisualTab('current');
    }, [post.id, project.id]);

    const onSetPostPrompt = useCallback(() => {
        // Blueprint edit ignored in Modal mode — no-op callback
    }, []);

    // --- Carousel Helpers & Actions ---
    const isCarouselPost = useCallback(() => {
        return localVisualData.visualSequence &&
               typeof localVisualData.visualSequence === 'object' &&
               !Array.isArray(localVisualData.visualSequence) &&
               (localVisualData.visualSequence.facebook?.length > 0 || localVisualData.visualSequence.instagram?.length > 0);
    }, [localVisualData.visualSequence]);

    const handleClearSlideVisual = async (platform, slideIndex) => {
        const confirmed = await confirm({
            title: 'Clear Slide Visual',
            message: `Clear visual for slide ${slideIndex + 1}? The blueprint will be preserved.`,
            confirmLabel: 'Clear',
            variant: 'danger'
        });
        if (!confirmed) return;
        
        try {
            const postRef = doc(db, 'projects', project.id, 'posts', post.id);
            const currentSequence = [...localVisualData.visualSequence[platform]];
            
            currentSequence[slideIndex] = {
                ...currentSequence[slideIndex],
                visual: null,
                status: 'pending'
            };
            
            await updateDoc(postRef, {
                [`visualSequence.${platform}`]: currentSequence,
                updatedAt: serverTimestamp()
            });
            
            const freshPostDoc = await getDoc(postRef);
            if (freshPostDoc.exists()) {
                setLocalVisualData(prev => ({ ...prev, visualSequence: freshPostDoc.data().visualSequence }));
            }

            addToast("Visual cleared. You can regenerate or upload a new image.", "info");
            setLocalError('');
        } catch (err) {
            setLocalError("Clear failed. Please try again.");
            addToast("Clear failed. Please try again.", "error");
        }
    };

    const handleRegenerateSlide = async (targetPlatform, slideIndex) => {
        setProcessingSlide({ platform: targetPlatform, slideIndex });
        setLocalError('');
        
        try {
            const currentSlide = localVisualData.visualSequence?.[targetPlatform]?.[slideIndex];
            if (!currentSlide?.blueprint) {
                throw new Error("Slide blueprint not found. Cannot regenerate without blueprint.");
            }
            
            await generateImageFromPromptFn({
                projectId: project.id,
                postId: post.id,
                platform: targetPlatform,
                slideIndex: slideIndex,
                prompt: currentSlide.blueprint,
                settings: {
                    seed: Math.floor(Math.random() * 4294967296)
                }
            });
            
            const postRef = doc(db, 'projects', project.id, 'posts', post.id);
            const freshPostDoc = await getDoc(postRef);
            if (freshPostDoc.exists()) {
                setLocalVisualData(prev => ({ ...prev, visualSequence: freshPostDoc.data().visualSequence }));
            }

            addToast(`Slide ${slideIndex + 1} regeneration complete`, "success");
        } catch (err) {
            setLocalError("Regeneration failed. Please try again.");
            addToast("Regeneration failed. Please try again.", "error");
        } finally {
            setProcessingSlide(null);
        }
    };

    const handleOpenStudioForSlide = useCallback((targetPlatform) => {
        setActivePlatform(targetPlatform);
        setActiveVisualTab('ai');
    }, []);

    if (!post) return null;

    const platformIcon = (activePlatform || platform) === 'facebook' ? <FiFacebook /> : <FiInstagram />;
    const platformColor = (activePlatform || platform) === 'facebook' ? 'text-blue-500' : 'text-pink-500';
    const postStatus = post.status === 'failed' ? 'failed' : (post.status === 'scheduled' ? 'ready' : 'draft');

    // Character count
    const charCount = formData.content.length;
    const maxChars = platform === 'facebook' ? 63206 : 2200;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-warm-dark/95 backdrop-blur-xl animate-fade-in">
            <div className="w-full max-w-7xl h-[92vh] bg-warm-gray/20 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-modal overflow-hidden flex flex-col">
                
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/5 flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div className={`flex items-center gap-2 px-4 py-2 bg-slate-blue/20 border border-slate-blue/30 rounded-full ${platformColor}`}>
                            {platformIcon}
                            <span className="text-xs font-bold uppercase tracking-wider">{platform}</span>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase ${
                            postStatus === 'ready'
                                ? 'bg-pastel-green-bg/30 text-pastel-green border border-pastel-green/30'
                                : postStatus === 'failed'
                                    ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                    : 'bg-warm-gray/30 text-cream/60 border border-white/10'
                        }`}>
                            {postStatus === 'ready' ? <FiCheckCircle size={14} /> : postStatus === 'failed' ? <FiAlertCircle size={14} /> : <FiClock size={14} />}
                            {postStatus}
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="p-2 hover:bg-white/5 rounded-full transition-all transform hover:scale-110 active:scale-95"
                        aria-label="Close"
                    >
                        <FiX className="text-cream/60 hover:text-cream" size={24} />
                    </button>
                </div>

                {/* Error Alert */}
                {localError && (
                    <div className="mx-6 mt-4 p-4 bg-soft-coral/10 border border-soft-coral/30 rounded-2xl flex items-center gap-3 text-soft-coral animate-fade-in">
                        <FiAlertCircle size={20} />
                        <span className="text-sm font-medium">{localError}</span>
                    </div>
                )}

                {/* Failed Post Error Report */}
                {post.status === 'failed' && post.lastAttemptError && (
                    <div className="mx-6 mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl animate-fade-in">
                        <p className="text-xs text-red-300 font-bold uppercase tracking-wider">Last Failure:</p>
                        <p className="text-xs text-red-200 mt-1">{post.lastAttemptError}</p>
                    </div>
                )}

                {/* Main Content - Split Screen */}
                <div className="flex-1 overflow-hidden flex flex-col lg:flex-row gap-6 p-6">
                    
                    {/* LEFT PANEL - Controls (40%) */}
                    <div className="lg:w-2/5 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
                        
                        {/* Caption Editor */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-black text-cream/40 uppercase tracking-widest">Post Caption</label>
                                <span className={`text-xs font-mono ${charCount > maxChars * 0.9 ? 'text-soft-coral' : 'text-cream/30'}`}>
                                    {charCount} / {maxChars}
                                </span>
                            </div>
                            <textarea
                                rows={8}
                                value={formData.content}
                                onChange={handleContentChange}
                                className="block w-full bg-warm-gray/20 text-cream border border-white/10 rounded-2xl shadow-sm focus:ring-2 focus:ring-slate-blue focus:border-slate-blue text-sm p-4 leading-relaxed resize-none transition-all"
                                placeholder="Write your caption here..."
                            />
                        </div>

                        {/* Schedule Picker */}
                        <div className="space-y-3">
                            <label className="text-xs font-black text-cream/40 uppercase tracking-widest">Release Time</label>
                            <input
                                type="datetime-local"
                                value={formData.scheduledTime}
                                onChange={handleDateChange}
                                className="block w-full bg-warm-gray/20 text-cream border border-white/10 rounded-2xl focus:ring-2 focus:ring-slate-blue focus:border-slate-blue text-sm p-3 transition-all"
                            />
                        </div>

                        {/* Visual Tabs */}
                        <div className="space-y-4">
                            <label className="text-xs font-black text-cream/40 uppercase tracking-widest">Visual Asset</label>
                            
                            <div className="flex gap-2 p-1 bg-warm-gray/30 rounded-2xl border border-white/5">
                                <button
                                    onClick={() => setActiveVisualTab('current')}
                                    className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                                        activeVisualTab === 'current'
                                            ? 'bg-slate-blue text-warm-dark shadow-glow'
                                            : 'text-cream/40 hover:text-cream hover:bg-white/5'
                                    }`}
                                >
                                    Current
                                </button>
                                <button
                                    onClick={() => setActiveVisualTab('ai')}
                                    className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                                        activeVisualTab === 'ai'
                                            ? 'bg-slate-blue text-warm-dark shadow-glow'
                                            : 'text-cream/40 hover:text-cream hover:bg-white/5'
                                    }`}
                                >
                                    <FiZap className="inline mr-1" size={12} />
                                    AI Studio
                                </button>
                                <button
                                    onClick={() => setActiveVisualTab('upload')}
                                    className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                                        activeVisualTab === 'upload'
                                            ? 'bg-slate-blue text-warm-dark shadow-glow'
                                            : 'text-cream/40 hover:text-cream hover:bg-white/5'
                                    }`}
                                >
                                    <FiUploadCloud className="inline mr-1" size={12} />
                                    Upload
                                </button>
                            </div>

                            {/* Tab Content */}
                            {activeVisualTab === 'upload' && (
                                <div className="p-6 bg-warm-gray/10 rounded-2xl border border-white/5">
                                    <input type="file" ref={fileInputRef} onChange={handleFileSelectAndUpload} className="hidden" accept="image/*"/>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-full py-4 bg-warm-gray/20 border-2 border-dashed border-slate-blue/30 text-slate-blue rounded-2xl font-bold text-xs uppercase tracking-widest hover:border-slate-blue hover:bg-slate-blue/10 transition-all"
                                    >
                                        Choose Local File
                                    </button>
                                    {isUploading && (
                                        <div className="mt-4 w-full bg-warm-gray/30 rounded-full h-2 overflow-hidden">
                                            <div className="bg-slate-blue h-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* AI Studio: controls inline (left panel) — preview stays on right */}
                            {activeVisualTab === 'ai' && (
                                <div className="bg-warm-gray/10 rounded-2xl border border-white/5 overflow-hidden">
                                    {isVideoTemplate(post.adjustments?.templates?.[activePlatform]) ? (
                                        <VideoGenerationPanel
                                            project={project}
                                            post={post}
                                            onVisualUpdate={handleVisualUpdateFromAI}
                                            adjustments={post.adjustments}
                                            activePlatform={activePlatform}
                                        />
                                    ) : (
                                        <VisualGenerationPanel
                                            project={project}
                                            post={post}
                                            onVisualUpdate={handleVisualUpdateFromAI}
                                            onSetPostPrompt={onSetPostPrompt}
                                            activePlatform={activePlatform}
                                            adjustments={post.adjustments}
                                            activeSlideIndex={activeSlideIndex}
                                            onSlideChange={setActiveSlideIndex}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT PANEL - Live Preview (60%) */}
                    <div className="lg:w-3/5 flex flex-col gap-4">
                        {(
                            <>
                                <div className="flex-1 flex items-center justify-center p-8 bg-warm-gray/10 backdrop-blur-sm rounded-3xl border border-white/5 shadow-card">
                                    {formData.visual?.url ? (
                                        <div className="relative group max-w-md w-full">
                                            <div className={`relative rounded-3xl overflow-hidden border border-white/10 shadow-card ${
                                            (activePlatform || platform) === 'facebook' ? 'aspect-square' : 'aspect-[4/5]' // Default to square if FB
                                        }`}>
                                                {(formData.visual.type === 'video' || formData.visual.metadata?.contentType === 'video/mp4') ? (
                                                    <video className="w-full h-full object-cover" controls>
                                                        <source src={formData.visual.url} type={formData.visual.metadata?.contentType || 'video/mp4'} />
                                                    </video>
                                                ) : (
                                                    <img
                                                        key={formData.visual.url}
                                                        src={`${formData.visual.url}${formData.visual.url.includes('?') ? '&' : '?'}_t=${Date.now()}`}
                                                        alt="Preview"
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-t from-warm-dark/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                                        <p className="text-cream text-sm line-clamp-3">{formData.content || 'No caption'}</p>
                                                    </div>
                                                </div>
                                                {formData.visual.metadata && (
                                                    <div className="absolute top-3 left-3">
                                                        <VisualBadge metadata={formData.visual.metadata} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 rounded-3xl pointer-events-none group-hover:pointer-events-auto">
                                                <button
                                                    onClick={() => setFormData(prev => ({...prev, visual: null}))}
                                                    className="p-4 bg-soft-coral text-white rounded-full hover:bg-soft-coral/80 shadow-glow transform hover:scale-110 transition-all"
                                                >
                                                    <FiTrash2 size={20}/>
                                                </button>
                                                {activeVisualTab !== 'ai' && (
                                                    <button
                                                        onClick={() => setActiveVisualTab('ai')}
                                                        className="p-4 bg-slate-blue text-warm-dark rounded-full hover:bg-slate-blue/80 shadow-glow transform hover:scale-110 transition-all"
                                                    >
                                                        <FiZap size={20}/>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ) : isCarouselPost() ? (
                                        <div className="w-full max-w-md space-y-4">
                                            {/* Platform Tabs for Carousel */}
                                            <div className="flex gap-2">
                                                {localVisualData.visualSequence?.facebook && (
                                                    <button 
                                                        onClick={() => { setActivePlatform('facebook'); setActiveSlideIndex(0); }}
                                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all ${
                                                            activePlatform === 'facebook' 
                                                            ? 'bg-blue-500 text-white shadow-glow' 
                                                            : 'bg-warm-gray/20 text-cream/60 hover:bg-warm-gray/30'
                                                        }`}
                                                    >
                                                        <FiFacebook /> Facebook
                                                    </button>
                                                )}
                                                {localVisualData.visualSequence?.instagram && (
                                                    <button 
                                                        onClick={() => { setActivePlatform('instagram'); setActiveSlideIndex(0); }}
                                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all ${
                                                            activePlatform === 'instagram' 
                                                            ? 'bg-pink-500 text-white shadow-glow' 
                                                            : 'bg-warm-gray/20 text-cream/60 hover:bg-warm-gray/30'
                                                        }`}
                                                    >
                                                        <FiInstagram /> Instagram
                                                    </button>
                                                )}
                                            </div>

                                            {/* Carousel Preview */}
                                            {(() => {
                                                const currentSequence = localVisualData.visualSequence?.[activePlatform] || [];
                                                const currentSlide = currentSequence[activeSlideIndex];
                                                const isProcessing = processingSlide?.platform === activePlatform && processingSlide?.slideIndex === activeSlideIndex;

                                                return (
                                                    <>
                                                        <div className="relative group rounded-3xl overflow-hidden border border-white/10 bg-warm-dark aspect-[4/5] shadow-card">
                                                            <div className="absolute top-3 left-3 bg-slate-blue text-warm-dark px-3 py-1.5 rounded-full text-xs font-black shadow-glow z-20">
                                                                Slide {activeSlideIndex + 1}/{currentSequence.length}
                                                            </div>
                                                            
                                                            <div className="absolute top-3 right-3 z-20">
                                                                {currentSlide?.status === 'ready' ? (
                                                                    <div className="bg-pastel-green-bg text-pastel-green px-2 py-1 rounded-full text-[10px] font-black">✓ READY</div>
                                                                ) : currentSlide?.status === 'queued' ? (
                                                                    <div className="bg-pale-lilac text-warm-dark px-2 py-1 rounded-full text-[10px] font-black">⏳ QUEUED</div>
                                                                ) : (
                                                                    <div className="bg-warm-gray/60 text-cream/60 px-2 py-1 rounded-full text-[10px] font-black">⏸ PENDING</div>
                                                                )}
                                                            </div>

                                                            {currentSlide?.visual?.url ? (
                                                                (currentSlide.visual.type === 'video' || currentSlide.visual.metadata?.contentType === 'video/mp4') ? (
                                                                    <video className="w-full h-full object-contain" controls>
                                                                        <source src={currentSlide.visual.url} type={currentSlide.visual.metadata?.contentType || 'video/mp4'} />
                                                                    </video>
                                                                ) : (
                                                                    <img src={currentSlide.visual.url} alt={`Slide ${activeSlideIndex + 1}`} className="w-full h-full object-contain"/>
                                                                )
                                                            ) : (
                                                                <div className="flex flex-col items-center justify-center h-full text-cream/40 p-6 text-center">
                                                                    <FiZap size={32} className="mb-3 opacity-30" />
                                                                    <span className="text-sm font-bold">Slide {activeSlideIndex + 1}</span>
                                                                    <span className="text-xs mt-1">{currentSlide?.status === 'queued' ? 'Generating...' : 'Pending Generation'}</span>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 z-30 pointer-events-none group-hover:pointer-events-auto">
                                                                <button 
                                                                    onClick={() => handleClearSlideVisual(activePlatform, activeSlideIndex)}
                                                                    className="px-4 py-3 bg-soft-coral text-white rounded-full hover:bg-soft-coral/80 shadow-glow transform hover:scale-110 transition-all flex items-center gap-2 text-xs font-black uppercase"
                                                                >
                                                                    <FiTrash2 size={16}/> Clear
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleRegenerateSlide(activePlatform, activeSlideIndex)}
                                                                    disabled={isProcessing}
                                                                    className="px-4 py-3 bg-slate-blue text-warm-dark rounded-full hover:bg-slate-blue/80 shadow-glow transform hover:scale-110 transition-all flex items-center gap-2 text-xs font-black uppercase disabled:opacity-50"
                                                                >
                                                                    <FiRefreshCw size={16} className={isProcessing ? 'animate-spin' : ''}/> Regen
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleOpenStudioForSlide(activePlatform)}
                                                                    className="px-4 py-3 bg-pastel-green-bg text-pastel-green rounded-full hover:bg-pastel-green-bg/80 shadow-glow transform hover:scale-110 transition-all flex items-center gap-2 text-xs font-black uppercase"
                                                                >
                                                                    <FiZap size={16}/> Studio
                                                                </button>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Filmstrip Navigation */}
                                                        <div className="flex gap-1.5 h-2">
                                                            {currentSequence.map((slide, idx) => {
                                                                let bgColor = 'bg-warm-gray/30';
                                                                if (slide.status === 'ready') bgColor = 'bg-pastel-green';
                                                                else if (slide.status === 'queued') bgColor = 'bg-pale-lilac/50';
                                                                if (idx === activeSlideIndex) bgColor = 'bg-slate-blue';
                                                                
                                                                return (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => setActiveSlideIndex(idx)}
                                                                        className={`flex-1 rounded-full transition-all duration-300 hover:scale-y-150 cursor-pointer ${bgColor} ${idx === activeSlideIndex ? 'ring-2 ring-slate-blue/50 shadow-glow' : 'opacity-60 hover:opacity-100'}`}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div className="text-center space-y-4">
                                            <FiZap size={48} className="mx-auto text-cream/20" />
                                            <p className="text-cream/40 text-sm font-medium">No visual asset</p>
                                            <p className="text-cream/30 text-xs">Use AI Studio or Upload to add an image</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-between p-6 border-t border-white/5 bg-warm-gray/10 backdrop-blur-sm flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 text-cream/60 hover:text-cream rounded-xl hover:bg-white/5 transition-all font-bold text-sm uppercase tracking-wider"
                    >
                        Cancel
                    </button>
                    <div className="flex items-center gap-3">
                        {post.status === 'failed' && (
                            <button
                                onClick={handlePublishNow}
                                disabled={isPublishing || isSaving}
                                className="px-8 py-3 bg-orange-500 text-white rounded-full shadow-glow inline-flex items-center gap-2 hover:bg-orange-600 transition-colors font-bold text-sm uppercase tracking-wider disabled:opacity-50"
                            >
                                {isPublishing ? <FiLoader className="animate-spin" size={18} /> : <FiRefreshCw size={18} />}
                                Publish Now
                            </button>
                        )}
                        <button
                            onClick={handleSaveChanges}
                            disabled={isSaving || isUploading || isPublishing}
                            className="btn btn-primary px-8 py-3 rounded-full shadow-glow inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {isSaving ? <FiLoader className="animate-spin" size={18} /> : <FiSave size={18} />}
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

EditPostModal.propTypes = {
    project: PropTypes.object.isRequired,
    post: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    initialPlatform: PropTypes.string 
};

export default EditPostModal;
